/**
 * @cfleet/api-client - Customer Password Reset Hooks
 *
 * React Query hooks for customer-specific password reset operations.
 * These hooks target the /customer/auth/password/* endpoints.
 * They are public endpoints (no auth required).
 *
 * @example
 * ```tsx
 * import { useCustomerRequestPasswordReset, useCustomerConfirmPasswordReset } from '@cfleet/api-client';
 *
 * // Request password reset
 * const { mutate: requestReset, isPending } = useCustomerRequestPasswordReset();
 * requestReset({ email: 'customer@example.com' }, {
 *   onSuccess: () => toast.success('Check your email for reset instructions'),
 * });
 *
 * // Confirm password reset (with token from email)
 * const { mutate: confirmReset } = useCustomerConfirmPasswordReset();
 * confirmReset({ token, new_password: newPassword }, {
 *   onSuccess: () => navigate('/login'),
 * });
 * ```
 */

import { useMutation } from '@tanstack/react-query';
import type { CustomerAuthComponents } from '../generated';
import { createCustomerClient, type ClientConfig } from '../index';

// ─────────────────────────────────────────────────────────────────────────────
// Type Aliases
// ─────────────────────────────────────────────────────────────────────────────

type PasswordResetRequest = CustomerAuthComponents['schemas']['PasswordResetRequest'];
type MessageResponse = CustomerAuthComponents['schemas']['MessageResponse'];
type PasswordResetConfirmRequest = CustomerAuthComponents['schemas']['PasswordResetConfirmRequest'];
type ErrorResponse = CustomerAuthComponents['schemas']['ErrorResponse'];

// ─────────────────────────────────────────────────────────────────────────────
// Configuration
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Configuration for customer password reset hooks
 */
export interface UseCustomerPasswordResetHooksConfig {
  /** Custom client configuration (optional) */
  clientConfig?: ClientConfig;
}

// ─────────────────────────────────────────────────────────────────────────────
// useCustomerRequestPasswordReset
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to request a password reset email for customers.
 *
 * This is a public endpoint (no auth required).
 * An email with reset instructions will be sent to the address if it exists.
 * For security, the response is always successful even if the email doesn't exist.
 *
 * Endpoint: POST /customer/auth/password/reset
 *
 * @param config - Optional configuration including custom client config
 * @returns Mutation hook for requesting password reset
 *
 * @example
 * ```tsx
 * const { mutate: requestReset, isPending, error, isSuccess } = useCustomerRequestPasswordReset();
 *
 * const handleSubmit = (email: string) => {
 *   requestReset({ email }, {
 *     onSuccess: () => {
 *       // Show "check your email" message
 *       setShowCheckEmail(true);
 *     },
 *     onError: (error) => {
 *       // Handle error (e.g., rate limiting)
 *       toast.error(error.message);
 *     },
 *   });
 * };
 * ```
 */
export function useCustomerRequestPasswordReset(config?: UseCustomerPasswordResetHooksConfig) {
  const client = createCustomerClient(config?.clientConfig);

  return useMutation({
    mutationFn: async (data: PasswordResetRequest): Promise<MessageResponse> => {
      const { data: response, error } = await client.POST('/customer/auth/password/reset', {
        body: data,
      });

      if (error) {
        // Error might be 400 (validation) or other server errors
        const errorObj = error as unknown;
        if (errorObj && typeof errorObj === 'object' && 'detail' in errorObj) {
          throw new Error((errorObj as ErrorResponse).detail || 'Failed to request password reset');
        }
        throw new Error('Failed to request password reset');
      }

      return response!;
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useCustomerConfirmPasswordReset
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to confirm a password reset with token and new password for customers.
 *
 * This is a public endpoint (no auth required).
 * The token is obtained from the password reset email link.
 *
 * Endpoint: POST /customer/auth/password/reset/confirm
 *
 * @param config - Optional configuration including custom client config
 * @returns Mutation hook for confirming password reset
 *
 * @example
 * ```tsx
 * const { mutate: confirmReset, isPending, error } = useCustomerConfirmPasswordReset();
 *
 * const handleSubmit = (token: string, newPassword: string) => {
 *   confirmReset({ token, new_password: newPassword }, {
 *     onSuccess: () => {
 *       toast.success('Password reset successfully!');
 *       navigate('/login');
 *     },
 *     onError: (error) => {
 *       // Handle error (e.g., expired token, weak password)
 *       toast.error(error.message);
 *     },
 *   });
 * };
 * ```
 */
export function useCustomerConfirmPasswordReset(config?: UseCustomerPasswordResetHooksConfig) {
  const client = createCustomerClient(config?.clientConfig);

  return useMutation({
    mutationFn: async (data: PasswordResetConfirmRequest): Promise<MessageResponse> => {
      const { data: response, error } = await client.POST('/customer/auth/password/reset/confirm', {
        body: data,
      });

      if (error) {
        // Error is defined in OpenAPI (400, 401 responses) with ErrorResponse schema
        const errorObj = error as ErrorResponse;
        throw new Error(errorObj.detail || 'Failed to reset password');
      }

      return response!;
    },
  });
}
